/**
 * <quick-add-sizes> — Pythia-style quick add bar on a product card.
 *
 * The bar either adds the single variant straight to the cart, or drops open a
 * list of sizes. Either way it writes the chosen variant into the card's existing
 * product form and submits it, so the theme's cart drawer, fly-to-cart animation
 * and error handling all still apply.
 */
class QuickAddSizes extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#handleClick, true);
    this.addEventListener('keydown', this.#handleKeydown);
    document.addEventListener('click', this.#handleOutsideClick);
    document.addEventListener('cart:update', this.#handleCartUpdate);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick, true);
    this.removeEventListener('keydown', this.#handleKeydown);
    document.removeEventListener('click', this.#handleOutsideClick);
    document.removeEventListener('cart:update', this.#handleCartUpdate);
    clearTimeout(this.resetTimeout);
  }

  get #form() {
    return this.closest('product-form-component')?.querySelector('form') ?? this.closest('form');
  }

  get #trigger() {
    return this.querySelector('.qa-bar__trigger');
  }

  #open(state) {
    this.toggleAttribute('open', state);
    this.#trigger?.setAttribute('aria-expanded', state ? 'true' : 'false');
  }

  #handleOutsideClick = (event) => {
    if (!this.hasAttribute('open')) return;
    if (this.contains(event.target)) return;
    this.#open(false);
  };

  #handleKeydown = (event) => {
    if (event.key === 'Escape' && this.hasAttribute('open')) {
      this.#open(false);
      this.#trigger?.focus();
    }
  };

  #handleClick = (event) => {
    const target = event.target.closest?.('[data-qa-toggle], [data-qa-variant]');
    if (!target || !this.contains(target)) return;

    event.preventDefault();
    event.stopPropagation();

    if (target.hasAttribute('data-qa-toggle')) {
      this.#open(!this.hasAttribute('open'));
      return;
    }

    if (target.disabled || target.getAttribute('aria-disabled') === 'true') return;

    this.#addToCart(target.dataset.qaVariant);
  };

  #addToCart(variantId) {
    const form = this.#form;
    if (!form || !variantId) return;

    const idInput = form.querySelector('input[name="id"]');
    if (!idInput) return;

    idInput.value = variantId;
    idInput.disabled = false;

    // The theme bails out of submit if any add-to-cart button is disabled,
    // which it is whenever the card's default variant happens to be sold out.
    form.querySelectorAll('add-to-cart-component button, button[name="add"]').forEach((button) => {
      button.disabled = false;
    });

    this.setAttribute('data-state', 'loading');
    this.pending = true;

    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
    } else {
      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    }

    clearTimeout(this.resetTimeout);
    this.resetTimeout = setTimeout(() => this.#reset(), 6000);
  }

  #handleCartUpdate = () => {
    if (!this.pending) return;

    this.setAttribute('data-state', 'added');
    this.#open(false);

    clearTimeout(this.resetTimeout);
    this.resetTimeout = setTimeout(() => this.#reset(), 1800);
  };

  #reset() {
    this.removeAttribute('data-state');
    this.pending = false;
  }
}

if (!customElements.get('quick-add-sizes')) {
  customElements.define('quick-add-sizes', QuickAddSizes);
}
